import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import {
  getMaxBatchUploadConfigApi,
  updateMaxBatchUploadConfigApi,
} from '@/lib/reports-api';
import {
  canSaveMaxBatchUpload,
  DEFAULT_MAX_BATCH_UPLOAD_RANGE,
  isMaxBatchUploadInRange,
  MAX_BATCH_UPLOAD_LABEL,
  maxBatchUploadRangeHint,
  type MaxBatchUploadRange,
} from '@/lib/max-batch-upload';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';

/**
 * Issue #385 — 管理员「系统设置 → 语音识别API → 批量上传最大文件数」。
 *
 * 真实 GET/PATCH /system/max-batch-upload-config（GET 任意已登录可读，PATCH 仅 ADMIN+，
 * 按 orgId 隔离）。该上限（默认 10，区间 1-50）即移动端质检中心批量上传本地录音时一次
 * 最多可选的文件数：超过明确报错、不静默截断；随后按队列串行逐个 ASR/AI。越界由服务端
 * 钳制；保存后运行时即时生效，无需重启。
 *
 * 渲染外的「能否保存 / 区间校验 / 文案」逻辑下沉到纯模块 @/lib/max-batch-upload（admin
 * vitest 无渲染夹具），本组件仅薄包装；绝不前端 state 模拟，数据全部来自真实接口。
 */
const INPUT =
  'w-28 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';

export function MaxBatchUploadSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['system', 'max-batch-upload-config'],
    queryFn: getMaxBatchUploadConfigApi,
  });
  const [draft, setDraft] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data) setDraft(String(query.data.maxBatchUploadFiles));
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (count: number) => updateMaxBatchUploadConfigApi(count),
    onSuccess: async (data) => {
      setSaveError(null);
      setSaved(true);
      setDraft(String(data.maxBatchUploadFiles));
      await queryClient.invalidateQueries({ queryKey: ['system', 'max-batch-upload-config'] });
    },
    onError: (err) =>
      setSaveError(
        err instanceof ApiError ? `${err.message}（HTTP ${err.status}）` : '保存失败，请重试。',
      ),
  });

  if (query.isLoading) {
    return (
      <Card title={MAX_BATCH_UPLOAD_LABEL}>
        <LoadingState label="正在加载批量上传最大文件数…" />
      </Card>
    );
  }
  if (query.isError || draft === null || !query.data) {
    return (
      <Card title={MAX_BATCH_UPLOAD_LABEL}>
        <ErrorState
          title="加载批量上传最大文件数失败"
          description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
          retry={() => void query.refetch()}
        />
      </Card>
    );
  }

  const range: MaxBatchUploadRange = {
    min: query.data.min ?? DEFAULT_MAX_BATCH_UPLOAD_RANGE.min,
    max: query.data.max ?? DEFAULT_MAX_BATCH_UPLOAD_RANGE.max,
    defaultMaxBatchUploadFiles:
      query.data.defaultMaxBatchUploadFiles ?? DEFAULT_MAX_BATCH_UPLOAD_RANGE.defaultMaxBatchUploadFiles,
  };
  const localInvalid = !isMaxBatchUploadInRange(draft, range);
  const canSave = canSaveMaxBatchUpload({
    canEdit,
    pending: mutation.isPending,
    raw: draft,
    range,
  });

  const onSave = () => {
    setSaved(false);
    setSaveError(null);
    // 只读 / 越界 / 保存中一律不触发真实 PATCH（与 canSaveMaxBatchUpload 同源）。
    if (!canSave) return;
    mutation.mutate(Number(draft.trim()));
  };

  return (
    <Card
      title={MAX_BATCH_UPLOAD_LABEL}
      description="质检中心批量上传本地录音时，一次最多可选的文件数。超过上限时移动端明确报错、不静默截断；随后按队列串行逐个 ASR / AI 评价（>2MiB 文件继续走可恢复分块上传）。默认 10 个，区间 1-50，保存后运行时即时生效。"
    >
      <dl className="mb-4 grid grid-cols-2 gap-y-2 text-sm text-slate-700 sm:grid-cols-3">
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">当前上限</dt>
          <dd className="mt-1 font-medium text-slate-900" data-testid="max-batch-upload-current">
            {query.data.maxBatchUploadFiles} 个
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">允许范围</dt>
          <dd className="mt-1 font-medium text-slate-900">
            {range.min}-{range.max} 个
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">出厂默认</dt>
          <dd className="mt-1 font-medium text-slate-900">{range.defaultMaxBatchUploadFiles} 个</dd>
        </div>
      </dl>

      <label className="flex items-center justify-between gap-3 text-sm">
        <span className="text-slate-600">
          {MAX_BATCH_UPLOAD_LABEL}
          <span className="ml-1 text-xs text-slate-400">（{maxBatchUploadRangeHint(range)}）</span>
        </span>
        <input
          className={INPUT}
          type="number"
          inputMode="numeric"
          min={range.min}
          max={range.max}
          step={1}
          value={draft}
          disabled={!canEdit}
          onChange={(e) => {
            setSaved(false);
            setDraft(e.target.value);
          }}
          data-testid="max-batch-upload-input"
        />
      </label>

      {localInvalid ? (
        <p className="mt-3 text-xs text-amber-600">
          请输入 {range.min}-{range.max} 之间的整数。
        </p>
      ) : null}

      {canEdit ? (
        <div className="mt-4 flex items-center gap-3">
          <button
            className={PRIMARY_BTN}
            onClick={onSave}
            disabled={!canSave}
            data-testid="max-batch-upload-save"
          >
            {mutation.isPending ? '保存中…' : '保存配置'}
          </button>
          {saved ? (
            <span className="text-xs text-emerald-600">已保存，批量上传上限即时生效。</span>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-500">仅 ADMIN 及以上角色可修改批量上传最大文件数。</p>
      )}
      {saveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {saveError}
        </div>
      ) : null}
    </Card>
  );
}
